import { GlobalStore, UserStore } from '@roc/feature-app-core';
import { makeObservable, override, flow, observable, computed } from 'mobx';
import { ApiResponse } from '@roc/feature-app-core';
import { BaseLoanTypeStore } from '../baseLoanTypeStore';
import { SinglePropertyQuoteBorrowerInformationStore } from './singlePropertyQuoteBorrowerInformationStore';
import { LoanEconomicsStore } from '../borrowerLoanSubmission/loanEconomics/loanEconomicsStore';
import { SinglePropertyPropertyStore } from '../borrowerLoanSubmission/propertyInformation/singlePropertyStore';
import { BorrowerEntityInformationStore } from '../borrowerEntityInformationStore';
import { SinglePropertyPropertiesStore } from './singlePropertyPropertiesStore';
import { mapSinglePropertyLoanPayload } from 'libs/feature-borrower-loan-submission/src/modules/newLoanApplication/utils/borrowerLoanSubmissionUtils';
import { LoanService, LoanSubmissionStep } from '@roc/feature-loans';
import { segmentAnalytics, SegmentTrackerEvent, isNotBlank, LoanSubType } from '@roc/feature-utils';
import { BorrowerInformationSteps, PropertyInformationSteps } from '../../utils/constants';
import { SinglePropertyQuoteBorrowerEntityInformationStore } from './singlePropertyQuoteBorrowerEntityInformationStore';

interface County {
  [key: string]: string[];
}
export class SinglePropertyQuoteStore extends BaseLoanTypeStore {
  loanEconomicsStore: LoanEconomicsStore;
  singlePropertyPropertyStore: SinglePropertyPropertyStore;
  quoteId: number
  allCounties: County;
  currentAttorney: any;
  attorneys: any[];
  private loanService: LoanService;
  private userStore: UserStore;

  constructor(globalStore: GlobalStore, userStore: UserStore) {
    super(globalStore);
    this.loanSubtype = LoanSubType.SINGLE_PROPERTY;
    this.isPublic = false;
    this.quoteId;
    this.attorneys;
    this.currentAttorney = { name: 'Private Lender Law', email: 'lawyer.privatelenderlaw@roccapital.com' };
    this.singlePropertyPropertyStore = new SinglePropertyPropertyStore(globalStore);
    this.borrowerInformationStore = new SinglePropertyQuoteBorrowerInformationStore(
      globalStore
    );
    this.borrowerEntityInformationStore = new SinglePropertyQuoteBorrowerEntityInformationStore(
      globalStore,
      this.borrowerInformationStore
    );
    this.loanEconomicsStore = new LoanEconomicsStore(globalStore, userStore, this.singlePropertyPropertyStore, this.borrowerInformationStore);
    this.propertiesStore = new SinglePropertyPropertiesStore(
      globalStore,
      this.borrowerInformationStore
    );
    this.userStore = userStore;
    this.loanService = new LoanService()
    makeObservable(this, {
      submitLoan: override,
      fetchStateCounties: flow,
      currentAttorney: observable,
      attorneys: observable,
      getAttorneys: flow,
      allCounties: observable,
      counties: computed,
      handleBack: override,
      handleNext: override,
      handleBorrowerEntityInformationNextStep: override,
    });
    this.initialize();
  }
  get counties(): { value: string, label: string }[] {
    const flattenedCounties = Object.values(this.allCounties)
      .reduce((acc, counties) => acc.concat(counties), [])
      .map(county => ({
        value: county.split(',')[0].trim(),
        label: county.trim()
      }));
    return flattenedCounties.sort((a, b) => a.value.localeCompare(b.value));
  }
  filterCountiesByState(state: string): { value: string, label: string }[] {
    const filteredCounties = this.allCounties[state] || [];
    return filteredCounties.map(county => ({
      value: county.split(',')[0].trim(),
      label: county.trim()
    }));
  }
  initialize() {
    this.resetQuoteForms();
    this.fetchStateCounties();
  }
  addMissingPropertiesToMainBorrower(mainBorrower) {
    const mainBorrowerForm = this.borrowerInformationStore?.mainBorrowerFormStore;
    return { ...mainBorrower, citizenshipStatus: mainBorrower?.citizenshipStatus ? mainBorrower?.citizenshipStatus : mainBorrowerForm?.form.fields.citizenshipStatus?.value, ficoProvidedAtOrigination: mainBorrowerForm?.form.fields.ficoProvidedAtOrigination?.value, personalGuarantor: mainBorrowerForm?.form.fields.personalGuarantor.value, }
  }
  resetQuoteForms() {
    this.borrowerEntityInformationStore.borrowerEntityFormStore.reset();
    this.borrowerInformationStore.reset();
    this.singlePropertyPropertyStore.propertyFormStore.reset();
    this.loanEconomicsStore.loanEconomicsFormStore.reset();
    this.loanEconomicsStore.displayedQuotes = [];
  }
  *fetchStateCounties() {
    try {
      const response: ApiResponse = yield this.loanService.getStateCounties();
      this.allCounties = response.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting state counties.',
      });
    }
  }
  *getAttorneys(state, allowedLoanType) {
    try {
      const economicsStore = this.loanEconomicsStore;
      const response: ApiResponse = yield this.loanService.getLenderAttorneysByState(
        state,
        economicsStore.quote.pricerLoanTerms.amount
      );
      const attorneys = JSON.parse(response.data?.data);
      this.attorneys = attorneys?.filter(x => (x.externalAllowed && x.allowedLoanTypes.includes(allowedLoanType)))
        .sort((a1, a2) => a1.investorAttorneyId - a2.investorAttorneyId);
      this.setDefaultPreferredClosingAttorney();
    } catch (error) {
      console.log({ error });
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting attorneys.',
      });
    }
  }
  setDefaultPreferredClosingAttorney() {
    if (
      isNotBlank(this.currentAttorney.name) && !this.isClosingAttorneyPresent(this.currentAttorney)
    ) {
      const newAttorney = this.isClosingAttorneyPresent(
        this.currentAttorney.name
      )
        ? this.currentAttorney
        : this.filterAttorneyByName('LaRocca Hornik Rosen & Greenberg LLP');
      if (newAttorney !== null) {
        this.currentAttorney = newAttorney;
      }
    }
  }
  private filterAttorneyByName(name: string) {
    const match = this.attorneys.find(attorney => {
      const attorneyName = attorney?.company?.name || '';
      return attorneyName.toLowerCase().includes(name.toLowerCase());
    });

    return match
      ? {
        name: match.company?.name || '',
        email: match.company?.email || ''
      }
      : null;
  }
  private isClosingAttorneyPresent(preferredClosingAttorney) {
    return this.attorneys.some(
      attorney => attorney?.company?.name === preferredClosingAttorney.name
    );
  }
  *submitLoan() {
    try {
      this.newPublicLoanId = null;
      const currentLoan = {
        loanSubType: this.loanSubtype,
        submittedFromPortal: this.userStore.userInformation.portalName,
      }
      const attorneyData = {
        attorneyName: this.currentAttorney.name,
        attorneyEmail: this.currentAttorney.email
      }
      const data = mapSinglePropertyLoanPayload(
        currentLoan,
        this.borrowerEntityInformationStore.mainEntity,
        [this.addMissingPropertiesToMainBorrower(this.borrowerInformationStore.borrowers[0]),
        ...this.borrowerInformationStore.borrowers.slice(1)],
        this.singlePropertyPropertyStore.propertyFormStore.getFormValues(),
        this.loanEconomicsStore.quote,
        this.userStore,
        attorneyData
      );
      const response = yield this.loanService.submitTermLoan(
        data
      );
      if (response.data?.status === "OK") {
        this.newPublicLoanId = response.data?.data?.loanId;
        this.loanBorrowersInfo = response.data?.data?.loanBorrowerInformation?.loanBorrowers?.rows;
        this.quoteId = response.data?.data?.loanId;
        // clear forms
        this.resetQuoteForms();
        segmentAnalytics.trackEvent({
          name: SegmentTrackerEvent.BORROWER_LOAN_SUBMISSION,
          userName: this.userStore?.userInformation?.username,
        });
        this.handleNext(LoanSubmissionStep.QUOTE_COMPLETED);
      }
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message:
          'Borrower Term Loan Submission Failed',
      });
      this.sendLoanSubmissionErrorEmail(
        error?.error?.response?.data?.error?.message ||
        error?.error?.message ||
        error
      );
    } finally {
      this.saved = true;
    }
  }

  sendLoanSubmissionErrorEmail(error: string) {
    this.loanService.sendErrorEmail(
      'Borrower Term Loan Submission Failed',
      `Error occured while submitting ${this.loanType} (${this.loanSubtype}) loan`,
      error || 'Error occured when submitting loan'
    );
  }



  handleBack = (currentStep: LoanSubmissionStep) => {
    if (currentStep === LoanSubmissionStep.PROPERTY_INFORMATION) {
      this.handlePropertyInformationBack();
    } else if (currentStep === LoanSubmissionStep.ENTITY_INFORMATION) {
      this.handleEntityInformationBack();
    } else if (currentStep === LoanSubmissionStep.BORROWER_INFORMATION) {
      this.handleBorrowerInformationBackStep();
    } else if (currentStep === LoanSubmissionStep.SUMMARY) {
      this.handleSummaryBack();
    } else {
      this.activeStep--;
    }
  };

  handleNext = (currentStep: LoanSubmissionStep) => {
    if (currentStep === LoanSubmissionStep.QUOTE_SUMMARY) {
      this.setActiveStep(LoanSubmissionStep.QUOTE_COMPLETED);
    }
    if (currentStep === LoanSubmissionStep.ENTITY_INFORMATION) {
      this.handleBorrowerEntityInformationNextStep();
    } else if (currentStep === LoanSubmissionStep.BORROWER_INFORMATION) {
      this.handleBorrowerInformationNextStep();
      if (
        this.borrowerInformationStore.borrowerInformationActiveStep ===
        BorrowerInformationSteps.BORROWER_INFORMATION_COMPLETED
      ) {
        this.setActiveStep(LoanSubmissionStep.SUMMARY);
      }
    } else if (currentStep === LoanSubmissionStep.PROPERTY_INFORMATION) {
      this.propertiesStore.handlePropertyInformationNextStep();
      if (
        this.propertiesStore.getPropertyInformationActiveStep() ===
        PropertyInformationSteps.PROPERTY_INFORMATION_COMPLETED
      ) {
        this.setActiveStep(LoanSubmissionStep.ENTITY_INFORMATION);
      }
    } else if (currentStep === LoanSubmissionStep.SUMMARY) {
      this.submitLoan();
    } else {
      this.activeStep++;
    }
  };

  handleBorrowerEntityInformationNextStep = () => {
    const borrowerEntityInformationStore = this.borrowerEntityInformationStore as SinglePropertyQuoteBorrowerEntityInformationStore;
    const steps = borrowerEntityInformationStore.getEntityInformationSteps();
    const entityStep = borrowerEntityInformationStore
      .borrowerEntityInformationActiveStep;
    const isLastStep = steps[steps.length - 1] === entityStep;

    if (!borrowerEntityInformationStore.validateCurrentStep()) {
      return;
    }

    if (isLastStep) {
      //this.setActiveStep(LoanSubmissionStep.LOAN_ECONOMICS);
      this.activeStep = 3;
    } else {
      borrowerEntityInformationStore.goToNextSubstep();
    }
  };

  handleEntityInformationBack = () => {
    const borrowerEntityInformationStore = this.borrowerEntityInformationStore as SinglePropertyQuoteBorrowerEntityInformationStore;
    const steps = borrowerEntityInformationStore.getEntityInformationSteps();
    const entityStep = borrowerEntityInformationStore
      .borrowerEntityInformationActiveStep;
    const isFirstStep = steps[0] === entityStep;

    if (isFirstStep) {
      this.setActiveStep(LoanSubmissionStep.PROPERTY_INFORMATION);
      this.propertiesStore.setPropertyInformationActiveStep(
        PropertyInformationSteps.EDIT_PROPERTY_INFORMATION
      );
    } else {
      borrowerEntityInformationStore.goToPreviousSubstep();
    }
  };
}
